// frontend/src/hooks/useDebounce.ts
import { useState, useEffect } from 'react';

/**
 * Hook customizado que aplica "debounce" a um valor.
 * Ele só atualiza o valor retornado se o 'value' de entrada
 * não mudar por um 'delay' especificado.
 *
 * @param value O valor a ser "debounced".
 * @param delay O tempo de espera em milissegundos (ms).
 * @returns O valor "debounced".
 */
function useDebounce<T>(value: T, delay: number): T {
  // Estado para armazenar o valor "debounced"
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    // Configura um temporizador para atualizar o valor
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Limpa o temporizador se o 'value' ou 'delay' mudarem
    // ou se o componente for desmontado
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]); // Re-executa apenas se 'value' ou 'delay' mudarem

  return debouncedValue;
}

export default useDebounce;